import { ensureSignedIn, providerIsSignedIn } from "../auth/microsoftAuth";
import { MS365Document } from "../types/types";
import {
  normalizeTimelineData,
  getExtensionFromString,
  getExtension
} from "./GraphTimelineNormalizer";

export const fetchTimelineData = async (
  search?: string,
  maxResults: number = 10,
  startDate?: Date,
  endDate?: Date,
  types?: string[]
): Promise<MS365Document[]> => {
  try {
    const provider = await ensureSignedIn();

    if (!provider || !providerIsSignedIn(provider.state)) return [];

    const graphClient = provider.graph.client;
    // For tasks, use Microsoft To Do API
    let tasksRequest: any = null;
    if (!types || types.includes("task")) {
      tasksRequest = graphClient
      .api("/me/todo/lists")
      .get()
      .then(async (listsRes: any) => {
        if (!listsRes.value || !listsRes.value.length) return [];
        const allTasks: any[] = [];
        for (const list of listsRes.value) {
        let tasksApi = graphClient
          .api(`/me/todo/lists/${list.id}/tasks`)
          .top(maxResults)
          .select([
          "id",
          "title",
          "status",
          "createdDateTime",
          "lastModifiedDateTime",
          "dueDateTime",
          "importance",
          "body",
          "webLink",
          ]);
        if (search && search.trim()) {
          // $filter for title contains search
          const filter = `contains(title,'${search.replace(/'/g, "''")}')`;
          tasksApi = tasksApi.filter(filter);
        }
        const tasksRes = await tasksApi.get().catch(() => ({ value: [] }));
        if (tasksRes.value && tasksRes.value.length) {
          tasksRes.value.forEach((task: any) => {
          task.listId = list.id;
          task.listName = list.displayName;
          });
          allTasks.push(...tasksRes.value);
        }
        }
        return allTasks;
      })
      .catch(() => []);
    }
    // For messages, use $search if search is provided, else just get top N
    let messagesRequest: any = null;
    if (!types || types.includes("email")) {
      messagesRequest = graphClient
        .api("/me/messages")
        .expand("attachments")
        .top(maxResults)
        .select([
          "id",
          "subject",
          "receivedDateTime",
          "bodyPreview",
          "webLink",
          "hasAttachments",
          "conversationId",
          "body", // Include the full body property
        ]);

      if (search && search.trim()) {
        // $search only works on certain fields, but covers subject and body
        messagesRequest = messagesRequest.search(`"${search}"`);
      }
    }

    // For events, use $filter on subject if search is provided
    let eventsRequest: any = null;
    if (!types || types.includes("event")) {
      eventsRequest = graphClient
        .api("/me/events")
        .expand("attachments")
        .top(maxResults)
        .select([
          "id",
          "subject",
          "start",
          "bodyPreview",
          "webLink",
          "hasAttachments",
          "body", // Include the full body property (contains contentType and content)
        ]);

      if (search && search.trim()) {
        // $filter for subject contains search (bodyPreview is not supported)
        const filter = `contains(subject,'${search.replace(/'/g, "''")}')`;
        eventsRequest = eventsRequest.filter(filter);
      }
    }

    const [emails, events] = await Promise.all([
      messagesRequest ? messagesRequest.get() : Promise.resolve({ value: [] }),
      eventsRequest ? eventsRequest.get() : Promise.resolve({ value: [] }),
    ]);

    // Search OneDrive and SharePoint for documents if search is provided
    let documents: any[] = [];
    if (search && search.trim()) {
      // OneDrive personal files
      // Filter OneDrive results by file extension if types are provided
      const oneDriveResults = await graphClient
        .api("/me/drive/root/search(q='" + encodeURIComponent(search) + "')")
        .top(maxResults)
        .select([
          "id",
          "name",
          "webUrl",
          "file",
          "parentReference",
          "lastModifiedBy",
          "size",
          "createdDateTime",
          "lastModifiedDateTime",
        ])
        .expand("thumbnails")
        .get()
        .then((res: any) => {
          if (!res.value) return { value: [] };
          if (types && types.length > 0) {
        // types is an array of extensions, e.g. ['pdf', 'docx']
        const lowerTypes = types.map(t => t.toLowerCase());
        return {
          value: res.value.filter((doc: any) => {
            const ext: string = ""; // getExtension(doc.name || "");
            return ext && lowerTypes.includes(ext.toLowerCase());
          }),
        };
          }
          return res;
        })
        .catch(() => ({ value: [] }));

      // SharePoint files (from followed sites, for example)
      // This is a basic example; adapt as needed for your tenant/sites
      const sharePointResults = await graphClient
        .api("/sites?search=" + encodeURIComponent(search))
        .top(5)
        .get()
        .then(async (sitesRes: any) => {
          if (!sitesRes.value || !sitesRes.value.length) return [];
          const allFiles: any[] = [];
          const lowerTypes = types ? types.map(t => t.toLowerCase()) : [];
          for (const site of sitesRes.value) {
        const files = await graphClient
          .api(
            `/sites/${site.id}/drive/root/search(q='${encodeURIComponent(
          search
            )}')`
          )
          .top(maxResults)
          .select([
            "id",
            "name",
            "webUrl",
            "file",
            "parentReference",
            "lastModifiedBy",
            "size",
            "createdDateTime",
            "lastModifiedDateTime",
          ])
          .expand("thumbnails")
          .get()
          .then((r: any) => {
            if (!r.value) return [];
            if (lowerTypes.length > 0) {
          return r.value.filter((doc: any) => {
            const ext: string = ""; // getExtensionFromString(doc.name || "");
            return ext && lowerTypes.includes(ext.toLowerCase());
          });
            }
            return r.value;
          })
          .catch(() => []);
        files.forEach((f: any) => {
          f.siteId = site.id;
        });
        allFiles.push(...files);
          }
          return allFiles;
        })
        .catch(() => []);

      documents = [
        ...(oneDriveResults.value || []).map((doc: any) => ({
          ...doc,
          driveType: "onedrive",
        })),
        ...(sharePointResults || []).map((doc: any) => ({
          ...doc,
          driveType: "sharepoint",
        })),
      ];
    }

    // Merge all results and normalize
    const tasks = tasksRequest ? await tasksRequest : [];
    return normalizeTimelineData(emails.value, events.value, documents, tasks);

    return null; // normalizeTimelineData([], [], documents);
  } catch (error) {
    console.error("Error fetching timeline data:", error);
    return [];
  }
};
